import { App as CapacitorApp, URLOpenListenerEvent } from '@capacitor/app';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Parses a URL string and calls the handler with the appropriate path.
 * Supports work4all:// custom scheme and https/http URLs with work4all or igenti.de domains.
 */
export const parseAndHandleUrl = (
  urlString: string,
  handleUrlOpen: (path: string) => void
) => {
  try {
    const url = new URL(urlString);

    // Handle different URL schemes
    if (url.protocol === 'work4all:') {
      // For work4all:// URLs, include the hostname in the path
      // e.g., work4all://t-1/more -> /t-1/more
      const hostname = url.hostname || '';
      const pathname = url.pathname || '/';
      const path = hostname ? `/${hostname}${pathname}` : pathname;
      handleUrlOpen(path);
    } else if (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      // Not sure if we even need to check the host as we defined domains in .well-known directories
      (url.host.toLowerCase().includes('work4all') ||
        url.host.toLowerCase().includes('igenti.de'))
    ) {
      const path = url.pathname + url.search + url.hash;
      handleUrlOpen(path);
    }
  } catch (error) {
    console.error('Error parsing URL:', error);
  }
};

// Navigate to the launch URL once
let wasInitializedWithLaunchUrl = false;

/**
 * Hook to handle deep linking and universal links in the Capacitor app.
 * This will intercept URLs from Universal Links (iOS) and App Links (Android)
 * and navigate within the app accordingly :).
 */
export function useDeepLinking() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleUrlOpen = (path: string) => {
      navigate(path);
    };

    let cleanupResolve: (
      value: (() => void) | PromiseLike<() => void>
    ) => void = undefined;
    const cleanupPromise = new Promise<() => void>((resolve) => {
      cleanupResolve = resolve;
    });

    // Listen for app URL open events (Universal Links, App Links, custom schemes)
    const setupListener = async () => {
      const listener = await CapacitorApp.addListener(
        'appUrlOpen',
        (event: URLOpenListenerEvent) => {
          parseAndHandleUrl(event.url, handleUrlOpen);
        }
      );

      cleanupResolve(() => listener.remove());
    };

    setupListener();

    // Check if app was launched with a URL
    if (!wasInitializedWithLaunchUrl) {
      wasInitializedWithLaunchUrl = true;
      CapacitorApp.getLaunchUrl().then((launchUrl) => {
        if (launchUrl?.url) {
          parseAndHandleUrl(launchUrl.url, handleUrlOpen);
        }
      });
    }

    // Cleanup listener on unmount
    return () => {
      cleanupPromise.then((resolved) => resolved());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
